import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosBackend from "../api/axiosBackend";

// shadcn
import { Popover, PopoverTrigger, PopoverContent } from "components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "components/ui/command";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Plus, X, ChevronDown } from "lucide-react";
import { getColorStyle } from "utils/tagColor";

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

const normalizeTagsResponse = (res) => {
  const data = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(data) ? data : [];
};

// Shared base for AutocompleteTagInput (order tag picker, allowCreate) and
// AutocompleteTagFilter (order-list filter, read-only selection) — the two
// were near-identical copies of this Popover+Command combobox.
export default function TagCombobox({
  selectedTags,
  setSelectedTags,
  placeholder = "חפש תג…",
  triggerLabel,
  triggerAriaLabel,
  allowCreate = false,
}) {
  const [allTags, setAllTags] = useState([]);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const inputRef = useRef(null);

  // initial fetch
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await axiosBackend.get("/tags/get");
        setAllTags(normalizeTagsResponse(res));
      } catch (e) {
        console.error("Error fetching tags:", e);
      }
    };
    fetchTags();
  }, []);

  // server search, only needed where new tags can be created on the fly
  useEffect(() => {
    if (!allowCreate) return;

    const fetchFiltered = async () => {
      const q = debouncedQuery.trim();
      if (!q) return;

      try {
        const res = await axiosBackend.get(`/tags/get?name=${encodeURIComponent(q)}`);
        const incoming = normalizeTagsResponse(res);

        setAllTags((prev) => {
          const map = new Map(prev.map((t) => [t._id, t]));
          incoming.forEach((t) => map.set(t._id, t));
          return Array.from(map.values());
        });
      } catch (e) {
        console.error("Error fetching filtered tags:", e);
      }
    };

    fetchFiltered();
  }, [allowCreate, debouncedQuery]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selectedIds = new Set((selectedTags || []).map((t) => t._id));

    const base = !q
      ? allTags
      : allTags.filter((t) => (t.name || "").toLowerCase().includes(q));

    return base.filter((t) => !selectedIds.has(t._id));
  }, [allTags, query, selectedTags]);

  const canCreate = useMemo(() => {
    if (!allowCreate) return false;
    const name = query.trim();
    if (!name) return false;
    return !allTags.some((t) => (t.name || "").toLowerCase() === name.toLowerCase());
  }, [allowCreate, allTags, query]);

  const handleSelect = (tag) => {
    if (!selectedTags.some((t) => t._id === tag._id)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setQuery("");
    setOpen(false);
  };

  const handleRemove = (id) => {
    setSelectedTags(selectedTags.filter((t) => t._id !== id));
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name) return;

    setCreating(true);
    try {
      const res = await axiosBackend.post("/tags/add", { name });
      const newTag = res?.data?.data ?? res?.data;
      if (!newTag?._id) return;

      setAllTags((prev) => [...prev, newTag]);
      handleSelect(newTag);
    } catch (e) {
      console.error("Error adding new tag:", e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="w-full space-y-2" dir="rtl">
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        {/* Trigger is an input-like button. One click opens; typing goes to focused CommandInput. */}
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 text-sm shadow-sm transition-colors hover:bg-accent/20 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={triggerAriaLabel}
          >
            <span className="text-muted-foreground">{triggerLabel}</span>
            <ChevronDown className="h-4 w-4 opacity-70" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0 max-h-[70vh] overflow-hidden"
          align="end"
          sideOffset={4}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            requestAnimationFrame(() => inputRef.current?.focus());
          }}
          onInteractOutside={(e) => {
            // CRITICAL: allow touch scrolling inside popover
            e.preventDefault();
          }}
        >

          <Command shouldFilter={false}>
            <div className="relative">
              <CommandInput
                ref={inputRef}
                placeholder={placeholder}
                value={query}
                onValueChange={setQuery}
              />

              {query.trim() && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 h-7 w-7 -translate-y-1/2"
                  onClick={() => {
                    setQuery("");
                    requestAnimationFrame(() => inputRef.current?.focus());
                  }}
                  aria-label="נקה חיפוש"
                  title="נקה"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <CommandList
              className="max-h-64 overflow-y-auto overscroll-contain touch-pan-y"
              style={{
                WebkitOverflowScrolling: "touch",
                touchAction: "pan-y",   // IMPORTANT for mobile
              }}
              onWheelCapture={(e) => e.stopPropagation()}
              onTouchMoveCapture={(e) => e.stopPropagation()} // mobile equivalent of wheel
            >
              {filtered.length === 0 ? (
                <CommandEmpty className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="text-sm text-muted-foreground">לא נמצאו תגיות</span>

                  {canCreate && (
                    <Button size="sm" onClick={handleCreate} disabled={creating}>
                      <Plus className="ml-1 h-4 w-4" />
                      {creating ? "יוצר…" : "צור"}
                    </Button>
                  )}
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {filtered.map((tag) => (
                    <CommandItem
                      key={tag._id}
                      value={tag.name}
                      onSelect={() => handleSelect(tag)}
                      className="cursor-pointer"
                    >
                      <span
                        className="ml-2 h-2.5 w-2.5 shrink-0 rounded-full bg-muted-foreground/40"
                        style={tag.color ? { backgroundColor: tag.color } : undefined}
                        aria-hidden="true"
                      />
                      {tag.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected tags */}
      <div className="flex flex-wrap gap-2">
        {selectedTags.map((tag) => {
          const colorStyle = getColorStyle(tag.color);
          return (
            <Badge
              key={tag._id}
              variant="secondary"
              className={`rounded-full px-3 py-1 text-sm ${colorStyle ? "" : "bg-primary/10 text-primary"}`}
              style={colorStyle || undefined}
            >
              <span>{tag.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-1 h-5 w-5 p-0"
                onClick={() => handleRemove(tag._id)}
                aria-label={`הסר תג ${tag.name}`}
                title="הסר"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </Badge>
          );
        })}
      </div>
    </div>
  );
}
